import React, { useEffect } from 'react';
import '@ant-design/v5-patch-for-react-19';
import { HomePage } from './components/home-page/home-page';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal, useMsalAuthentication } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { scopes } from './authConfig';
import './App.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import { Skeleton, App as AntdApp, ConfigProvider, theme } from 'antd';
import { Conflicts } from './components/conflicts/conflicts';
import { BookingPage } from './components/booking-page/booking-page';
import { useHeaderState } from './store/header-state';
import { CaseListPage } from './components/case-list-page/case-list-page';
import { OpportunityBulkUploadPage } from './components/case-bulk-upload-page/case-bulk-upload-page';
import { AdminPage } from './components/admin-page/admin-page';
import { QualificationConfigPage } from './components/qualification-config-page/qualification-config-page';
import { LostReasonConfigPage } from './components/lost-reason-config-page/lost-reason-config-page';
import { AutodialerConfigPage } from './components/autodialer-config-page/autodialer-config-page';
import { HasRole, Role } from './utils/auth-utils';
import { MantineProvider, createTheme } from '@mantine/core';
import { InboundCenter } from './components/inbound-center/inbound-center';
import ReportingPage from './components/reporting/reporting-page';
import { ProfilePage } from './components/profile-page/profile-page';
import { useShallow } from 'zustand/shallow';
import { ScriptPage } from './components/script-editor/script-page';
import { EditContactPage } from './components/admin-page/components/edit-contacts/edit-contacts-page';
import { TeamReportingPage } from './components/reporting/team-reporting-page';
import { SalesAgentsPage } from './components/sales-agents-page/sales-agents-page';
import ReportingPageWithTabs from './components/reporting/reporting-tabs';
import { Ums } from './components/ums/ums';
import NotFoundPage from './components/not-found-page/not-found-page';
import AccessDeniedPage from './components/access-denied-page/access-denied-page';

const App: React.FC = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();

  let isAdmin,
    isManager,
    isInboundAgent,
    isReportingAgent,
    isTeamLead = false;

  //NOTE: We need to keep this header state here so that when the language refreshes, it refreshes in all the components
  const [lang, setLang, currentTheme] = useHeaderState(useShallow((state) => [state.language, state.setLanguage, state.theme]));

  // Uses the root url as the redirect url for MSAL, so we only have to configure one redirectUri in Microsoft Entra.
  const redirectUri = window.location.origin;

  useMsalAuthentication(InteractionType.Redirect, {
    // Sends the full url being browsed in the state login request parameter.
    // After the login is done, MSAL will return the state and we'll use to redirect the browser to the original url requested by the user.
    state: window.location.toString(),
    redirectUri,
    scopes: scopes.salesAppointmentApi,
  });

  useEffect(() => {
    instance.handleRedirectPromise().then((response) => {
      // After the login is done MSAL returns whatever we sent in the "state" parameter on the login request.
      // Since we sent the original url there, parse it and tell react to navigate the original route that was requested.
      if (response?.state) {
        const redirectTo = response.state.replaceAll(redirectUri, '');
        navigate(redirectTo);
      }
    });
  }, [instance]);

  if (isAuthenticated) {
    const userAccounts = instance.getAllAccounts();
    if (userAccounts && userAccounts.length > 0) {
      instance.setActiveAccount(userAccounts[0]);
      isReportingAgent = HasRole(userAccounts, Role.LiveReporter);
      isAdmin = HasRole(userAccounts, Role.BookingAdmin);
      isManager = HasRole(userAccounts, Role.BookingManager);
      isInboundAgent = HasRole(userAccounts, Role.InboundAgent);
      isTeamLead = HasRole(userAccounts, Role.TeamLead);
    }
  }
  // Get the query parameters from the url for the standalone calendar page.
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search');

  // Configure Mantine theme
  const mantineTheme = createTheme({
    // Mantine v7 uses forceColorScheme prop on MantineProvider instead
  });

  // Configure Ant Design theme
  const antdTheme = {
    algorithm: currentTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
  };

  // Apply theme to document root for CSS custom properties
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);
    document.documentElement.style.colorScheme = currentTheme;
  }, [currentTheme]);

  return (
    <>
      <UnauthenticatedTemplate>
        <div className="container">
          <Skeleton paragraph={{ rows: 10 }} />
        </div>
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <ConfigProvider theme={antdTheme}>
          <MantineProvider theme={mantineTheme} forceColorScheme={currentTheme}>
            <AntdApp>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/booking" element={<BookingPage />} />
                <Route path="/edit-contact" element={<EditContactPage />} />
                <Route path="/profile" element={isAdmin || isReportingAgent ? <ProfilePage /> : <AccessDeniedPage />} />
                <Route path="/team-reporting" element={isAdmin || isManager ? <TeamReportingPage /> : <AccessDeniedPage />} />
                <Route
                  path="/reporting"
                  element={(() => {
                    const hasAdvancedAccess = isTeamLead || isAdmin || isManager;
                    const hasAccess = hasAdvancedAccess || isReportingAgent;

                    if (!hasAccess) return <AccessDeniedPage />;
                    if (hasAdvancedAccess) return <ReportingPageWithTabs />;
                    return <ReportingPage />;
                  })()}
                />
                <Route path="/inbound" element={isAdmin || isInboundAgent ? <InboundCenter searchInput={search} /> : <AccessDeniedPage />} />
                <Route path="/conflicts" element={isAdmin || isManager ? <Conflicts /> : <AccessDeniedPage />} />
                <Route path="/exports" element={isAdmin || isManager ? <CaseListPage /> : <AccessDeniedPage />} />
                <Route path="/scripts" element={isAdmin ? <ScriptPage /> : <AccessDeniedPage />} />
                <Route path="/bulk-import-opportunities" element={isAdmin ? <OpportunityBulkUploadPage /> : <AccessDeniedPage />} />
                <Route path="/sales-agents" element={isAdmin || isManager ? <SalesAgentsPage searchInput={search} /> : <AccessDeniedPage />} />
                <Route path="/admin" element={isAdmin ? <AdminPage /> : <AccessDeniedPage />} />
                <Route path="/configuration/autodialer" element={isAdmin ? <AutodialerConfigPage /> : <AccessDeniedPage />} />
                <Route path="/qualification-config" element={isAdmin || isManager ? <QualificationConfigPage /> : <AccessDeniedPage />} />
                <Route path="/lost-reason-config" element={isAdmin ? <LostReasonConfigPage /> : <AccessDeniedPage />} />
                <Route path="/ums" element={isAdmin || isManager ? <Ums /> : <AccessDeniedPage />} />
                {/* Catch-all route for 404 pages */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AntdApp>
          </MantineProvider>
        </ConfigProvider>
      </AuthenticatedTemplate>
    </>
  );
};

export default App;
